import { haystackMatchesCommaSearch } from "@/lib/manufacturing/commaDelimitedSearch";
import { matchesLooseCatalogQuery } from "@/lib/manufacturing/looseCatalogQuery";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { MfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";
import { parseMfgMsdsStatus } from "@/lib/manufacturing/msdsStatus";

export type InventoryActiveFilter = "active" | "all";
export type InventoryMsdsStatusFilter = "all" | MfgMsdsStatus;
export type InventoryRecipeUsageFilter = "all" | "unused";
/** Purchaser triage: materials with no on-hand quantity. */
export type InventoryQtyFilter = "all" | "zero";
/** Purchaser triage: materials with no unit cost entered. */
export type InventoryPriceFilter = "all" | "zero";

export function filterManufacturingInventoryMaterials(
  materials: readonly MfgMaterial[],
  opts: {
    search: string;
    category: string;
    activeFilter: InventoryActiveFilter;
    msdsStatusFilter: InventoryMsdsStatusFilter;
    recipeUsageFilter?: InventoryRecipeUsageFilter;
    qtyFilter?: InventoryQtyFilter;
    priceFilter?: InventoryPriceFilter;
    /** Material IDs referenced on any recipe BOM (direct or via Part use). */
    usedInRecipeIds?: ReadonlySet<string>;
    /** When false and unused filter is on, hide all rows until usage data loads. */
    usedInRecipeIdsReady?: boolean;
  },
): MfgMaterial[] {
  const recipeUsageFilter = opts.recipeUsageFilter ?? "all";
  const qtyFilter = opts.qtyFilter ?? "all";
  const priceFilter = opts.priceFilter ?? "all";
  const usedIds = opts.usedInRecipeIds ?? new Set<string>();
  const usageReady = opts.usedInRecipeIdsReady !== false;

  return materials.filter((m) => {
    const blob = [m.name, m.code, m.category].join(" ");
    const matchesSearch =
      haystackMatchesCommaSearch(blob, opts.search) || matchesLooseCatalogQuery(blob, opts.search);
    const cat = (m.category || "Uncategorized").trim();
    const matchesCategory = opts.category === "all" || cat === opts.category;
    const matchesActive = opts.activeFilter === "all" || m.is_active;
    const matchesMsds =
      opts.msdsStatusFilter === "all" ||
      parseMfgMsdsStatus(m.msds_status) === opts.msdsStatusFilter;
    const matchesUnused =
      recipeUsageFilter === "all" || (usageReady && !usedIds.has(m.id));
    const matchesQty =
      qtyFilter === "all" || (Number(m.on_hand_qty) || 0) <= 0;
    const matchesPrice =
      priceFilter === "all" || (Number(m.unit_cost) || 0) <= 0;
    return (
      matchesSearch &&
      matchesCategory &&
      matchesActive &&
      matchesMsds &&
      matchesUnused &&
      matchesQty &&
      matchesPrice
    );
  });
}
